<template>
  <div
    v-if="isVisible"
    id="main-banner"
    v-on-display="{ sectionInfo, context: this }"
    class="live-big-banner-catalog"
  >
    <intersection-observer
      @observer-in="handleObserverIn"
      @observer-out="handleObserverOut"
    >
      <h2 tabindex="0" class="ir">LIVE 빅배너</h2>
      <template v-if="!isSingleCarousel">
        <div class="swiper-pagination-box">
          <div v-if="hasCellList" class="swiper-pagination-big-banner" />
        </div>
      </template>
      <swiper
        :options="swiperOptions"
        @transition-end="onSlideChange"
        @ready="onReady"
      >
        <swiper-slide
          v-for="(cell, index) in cellList"
          :key="`${index}__${cell?.alt ?? ''}`"
          class="swiper swiper-container-horizontal"
        >
          <LiveBigBannerPlayerCell
            :cell-info="cell"
            :is-active="isVideoPlay && index === activeIndex"
            :thumbnail-info="thumbnailInfo"
          />
        </swiper-slide>
        <div slot="button-prev" class="swiper-button-prev"></div>
        <div slot="button-next" class="swiper-button-next"></div>
      </swiper>
    </intersection-observer>
  </div>
</template>
<script>
  import { swiper, swiperSlide } from 'vue-awesome-swiper'
  import { onDisplay } from '@/common/resources/directives'
  import CellMixin from '@/mixins/cell.mixin'
  import { convertBand } from '@/common/converter/convert-band'
  import LiveBigBannerPlayerCell from '@/components/model/cell/instant-player/LiveBigBannerPlayerCell.vue'
  import {
    COMMON_SWIPER_OPTIONS,
    SLIDES_PER_GROUP,
  } from '@/common/resources/swiper.const'
  import { isLogin } from '@/common/utils/credential/is-login'
  import IntersectionObserver from '@/components/observer/IntersectionObserver.vue'

  export default {
    name: 'LiveBigBannerCatalog',
    components: {
      IntersectionObserver,
      LiveBigBannerPlayerCell,
      swiper,
      swiperSlide,
    },
    directives: {
      onDisplay,
    },
    mixins: [CellMixin],
    props: {
      catalogInfo: {
        type: Object,
        default: () => {},
      },
      sectionInfo: {
        type: Object,
        default: () => {},
      },
      thumbnailInfo: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        cellList: [],
        bandInfo: null,
        activeIndex: -1,
        isLoginUser: isLogin(),
        isObserved: false,
        isVisible: true,
        swiperOptions: {
          spaceBetween: COMMON_SWIPER_OPTIONS.SPACE_BETWEEN,
          slidesPerView: COMMON_SWIPER_OPTIONS.SLIDES_PER_VIEW,
          slidesPerGroup: SLIDES_PER_GROUP.BIGBANNER,
          centeredSlides: true,
          autoplayDisableOnInteraction: false,
          nextButton: '.swiper-button-next',
          prevButton: '.swiper-button-prev',
          parallax: true,
          a11y: true,
          paginationBulletMessage: '{{index}}번째 배너',
          preloadImages: false,
          lazyLoading: true,
          lazyLoadingInPrevNext: true,
        },
      }
    },
    computed: {
      hasCellList() {
        return this.cellList.length > 0
      },
      isSingleCarousel() {
        return this.cellList.length === 1
      },
      isVideoPlay() {
        return this.isLoginUser && this.isObserved
      },
    },
    beforeDestroy() {
      this.swiper && this.swiper.destroy && this.swiper.destroy()
    },
    mounted() {
      this.isVisible = false
      this.setData()
    },
    methods: {
      handleObserverIn() {
        this.isObserved = true
      },
      handleObserverOut() {
        this.isObserved = false
      },
      onReady(swiper) {
        if (swiper.height > 0) {
          this.activeIndex = swiper.activeIndex
        }
      },
      async setData() {
        try {
          this.bandInfo = await convertBand(this.catalogInfo)
          this.cellList = this.bandInfo?.band?.celllist ?? []
          this.isVisible = true
        } catch (e) {
          this.isVisible = false
        }
      },
      getAltText(cell) {
        const [title] = cell?.title_list ?? []
        return title?.text ?? ''
      },
      onSlideChange(swiper) {
        // todo: event 확인 (slide-change, transition-end)
        this.activeIndex = swiper.activeIndex
      },
    },
  }
</script>
<style lang="scss" scoped>
  .live-big-banner-catalog {
    position: relative;
    overflow: hidden;
    padding: 0;
    margin-bottom: 80px;
    .title {
      padding: 0 100px;
    }
    .btn-more.all {
      right: 100px;
    }
    .swiper-slide {
      width: 60%;
      max-width: 1536px;
      min-width: 864px;
      aspect-ratio: 16/7;
    }
    .swiper-button-prev,
    .swiper-button-next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 20;
      width: 36px;
      height: 36px;
      background-repeat: no-repeat;
      background-color: transparent;
      background-size: cover;
      font-size: 0;
    }
    .swiper-button-next {
      right: calc(-20% + 100px + 54px);
      @media (min-width: 2560px) {
        right: calc((-100% + (2560px * 0.6)) / 2 + 100px + 54px);
      }
      background-image: url(../../../assets/images/main/arrow-banner-right.png);
      &:hover {
        background-image: url(../../../assets/images/main/arrow-banner-right-over.png);
      }
      .swiper-button-disabled:hover {
        opacity: 0;
      }
    }
    .swiper-button-prev {
      left: calc(20% - 100px + 54px);
      @media (min-width: 2560px) {
        left: calc((100% - (2560px * 0.6)) / 2 - 100px + 54px);
      }
      background-image: url(../../../assets/images/main/arrow-banner-left.png);
      &:hover {
        background-image: url(../../../assets/images/main/arrow-banner-left-over.png);
      }
      .swiper-button-disabled:hover {
        background-image: url(../../../assets/images/main/arrow-banner-left.png);
      }
    }
    .swiper-button-box {
      position: absolute;
      left: 100px;
      right: 100px;
      top: 50%;
      margin: 0 auto;
      transform: translate(0, -50%);
      z-index: 20;
    }
    .swiper-container {
      position: relative;
      min-width: 1440px;
      overflow: visible;
      // live big banner cell 최소 최대 사이즈에 대한 대응
      transform: translate3d(calc(-20% + 100px), 0, 0);
      @media (min-width: 2560px) {
        transform: translate3d(
          calc((-100% + (2560px * 0.6)) / 2 + 100px),
          0,
          0
        );
      }
    }
    &.autoplay-band {
      min-height: 421px;
    }
  }
</style>
